import { useMutation, useQueryClient } from "@tanstack/react-query";
import { httpClient } from "@/shared/api/http-client";
import type {
  AdminTranslation,
  CreateTranslationInput,
  UpdateTranslationInput,
} from "../shared/communications-types";
import { COMMUNICATIONS_QUERY_KEY } from "../catalog/use-communications";
import { COMMUNICATION_VARIABLES_QUERY_KEY } from "./use-communication-variables";

// The live preview renders the editor buffer client-side (see
// `render-template.ts` + `PreviewPanel`), so the `POST …/preview` endpoint is
// no longer consumed — previewing no longer requires saving a version.

interface UpdateTranslationParams {
  readonly translationId: string;
  readonly input: UpdateTranslationInput;
}

/** Edit copy → backend creates a NEW active version (never mutates). */
async function updateTranslation({
  translationId,
  input,
}: UpdateTranslationParams): Promise<AdminTranslation> {
  return httpClient<AdminTranslation>(`/api/admin/communications/translations/${translationId}`, {
    method: "PATCH",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(input),
  });
}

export function useUpdateTranslation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateTranslation,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: COMMUNICATIONS_QUERY_KEY });
      // A new/activated version may reference different {{variables}} — refresh
      // the preview's variable descriptor so its sample-data hint isn't stale.
      queryClient.invalidateQueries({
        queryKey: COMMUNICATION_VARIABLES_QUERY_KEY,
      });
    },
  });
}

/** Activate a specific version (rollback/forward), deactivating siblings. */
async function activateVersion(translationId: string): Promise<AdminTranslation> {
  return httpClient<AdminTranslation>(
    `/api/admin/communications/translations/${translationId}/activate`,
    { method: "POST" },
  );
}

export function useActivateVersion() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: activateVersion,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: COMMUNICATIONS_QUERY_KEY });
      // A new/activated version may reference different {{variables}} — refresh
      // the preview's variable descriptor so its sample-data hint isn't stale.
      queryClient.invalidateQueries({
        queryKey: COMMUNICATION_VARIABLES_QUERY_KEY,
      });
    },
  });
}

interface CreateTranslationParams {
  readonly channelId: string;
  readonly input: CreateTranslationInput;
}

/** Create the first translation for a `(channel, locale)` (API-COMM-2). */
async function createTranslation({
  channelId,
  input,
}: CreateTranslationParams): Promise<AdminTranslation> {
  return httpClient<AdminTranslation>(
    `/api/admin/communications/channels/${channelId}/translations`,
    {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(input),
    },
  );
}

export function useCreateTranslation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createTranslation,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: COMMUNICATIONS_QUERY_KEY });
      // A new/activated version may reference different {{variables}} — refresh
      // the preview's variable descriptor so its sample-data hint isn't stale.
      queryClient.invalidateQueries({
        queryKey: COMMUNICATION_VARIABLES_QUERY_KEY,
      });
    },
  });
}
